import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, 
  Truck, 
  Users,
  Megaphone,
  ChevronRight,
  Zap,
  TrendingUp,
  Calendar
} from 'lucide-react';

const ModuleSwitch = ({ currentModule, onModuleChange, isAdmin, integratedMode = false, theme }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  // Mapping module → route du dashboard correspondant
  const MODULE_DASHBOARDS = {
    vente: '/',
    marketing: '/marketing',
    logistique: '/ops',
    rh: '/employes'
  };

  // Change le module ET navigue vers son dashboard
  const handleModuleChange = (moduleId) => {
    onModuleChange(moduleId);
    const dashboardRoute = MODULE_DASHBOARDS[moduleId] || '/';
    navigate(dashboardRoute);
  };

  if (!isAdmin) {
    return null; // Seuls les admins peuvent voir le switch
  }

  const modules = [
    {
      id: 'vente',
      name: 'Vente',
      icon: ShoppingCart,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      description: 'Prospects, devis, commercial'
    },
    {
      id: 'marketing',
      name: 'Marketing',
      icon: Megaphone,
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-50',
      textColor: 'text-pink-700',
      description: 'Campagnes, Ads, SEO'
    },
    {
      id: 'logistique',
      name: 'Opérations',
      icon: Truck,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
      description: 'Chantiers, SAV, équipes, stock'
    },
    {
      id: 'rh',
      name: 'RH',
      icon: Users,
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700',
      description: 'Employés, congés, formations'
    }
  ];

  const currentModuleData = modules.find(m => m.id === currentModule) || modules[0];
  const CurrentIcon = currentModuleData.icon;

  // Mode intégré pour la sidebar
  if (integratedMode && theme) {
    return (
      <div className="relative">
        {/* Bouton Module Intégré - style Pilot compact */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full text-left"
          style={{
            background: 'rgba(246,248,250,0.9)',
            border: '0.5px solid rgba(227,232,238,1)',
            borderRadius: '12px',
            padding: '10px 12px',
            transition: 'all 0.2s'
          }}
        >
          <div className="flex items-center" style={{ gap: '10px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'rgba(31,209,253,0.1)', color: '#0E7A99', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <CurrentIcon className="w-4 h-4" style={{ color: '#0E7A99' }} />
            </div>
            <div className="flex-1">
              <div style={{ fontSize: '8.5px', letterSpacing: '1.2px', color: '#697386', fontWeight: 500 }}>MODULES</div>
              <div style={{ fontSize: '12px', color: '#1A1F36', fontWeight: 500 }}>{theme.name}</div>
            </div>
            <ChevronRight style={{ width: '12px', height: '12px', color: '#697386', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'none' }} />
          </div>
        </button>

        {/* Dropdown Menu pour changer de module */}
        {isExpanded && (
          <div className="absolute top-full left-0 mt-2 w-full bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
            <div className="p-3 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                <Zap className="w-4 h-4 text-primary" />
                Changer le module
              </h3>
            </div>
            
            <div className="p-2">
              {modules.map((module) => {
                const Icon = module.icon;
                const isActive = module.id === currentModule;
                
                return (
                  <button
                    key={module.id}
                    onClick={() => {
                      handleModuleChange(module.id);
                      setIsExpanded(false);
                    }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200
                      ${isActive 
                        ? `${module.bgColor} ${module.textColor} border border-current border-opacity-20` 
                        : 'hover:bg-gray-50 text-gray-700'
                      }
                    `}
                  >
                    <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${module.color} flex items-center justify-center`}>
                      <Icon className="w-3 h-3 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium text-sm">{module.name}</div>
                      <div className="text-xs opacity-75">{module.description}</div>
                    </div>
                    {isActive && (
                      <div className="w-2 h-2 bg-current rounded-full opacity-60"></div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Switch Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`
          flex items-center gap-3 px-4 py-2 rounded-xl transition-all duration-300
          ${currentModuleData.bgColor} ${currentModuleData.textColor}
          hover:shadow-md transform hover:scale-105
          border border-opacity-20 ${currentModuleData.textColor.replace('text-', 'border-')}
          relative overflow-hidden
        `}
      >
        {/* Background gradient animation */}
        <div className={`absolute inset-0 bg-gradient-to-r ${currentModuleData.color} opacity-10 transition-opacity duration-300`}></div>
        
        <div className="relative flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${currentModuleData.color} flex items-center justify-center`}>
            <CurrentIcon className="w-4 h-4 text-white" />
          </div>
          
          <div className="text-left">
            <div className="font-semibold text-sm">{currentModuleData.name}</div>
            <div className="text-xs opacity-75">{currentModuleData.description}</div>
          </div>
          
          <ChevronRight 
            className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`}
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isExpanded && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden fade-in">
          <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Zap className="w-4 h-4 text-primary" />
              Modules SolarCRM
            </h3>
            <p className="text-xs text-gray-600 mt-1">Basculez entre les différents modules</p>
          </div>
          
          <div className="p-2">
            {modules.map((module, index) => {
              const Icon = module.icon;
              const isActive = module.id === currentModule;
              
              return (
                <button
                  key={module.id}
                  onClick={() => {
                    handleModuleChange(module.id);
                    setIsExpanded(false);
                  }}
                  className={`
                    w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-300
                    ${isActive 
                      ? `${module.bgColor} ${module.textColor} shadow-md scale-[1.02]` 
                      : 'hover:bg-gray-50 text-gray-700 hover:scale-[1.01]'
                    }
                    relative overflow-hidden group
                  `}
                  style={{
                    animationDelay: `${index * 100}ms`
                  }}
                >
                  {/* Animated background */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${module.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                  
                  <div className="relative flex items-center gap-4 flex-1">
                    <div className={`
                      w-12 h-12 rounded-xl bg-gradient-to-br ${module.color} 
                      flex items-center justify-center shadow-lg
                      transform transition-transform duration-300 group-hover:scale-110
                    `}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    
                    <div className="flex-1 text-left">
                      <div className="font-semibold text-base">{module.name}</div>
                      <div className="text-sm opacity-75">{module.description}</div>
                    </div>
                    
                    {isActive && (
                      <div className="w-3 h-3 rounded-full bg-gradient-to-r from-green-400 to-green-500 shadow-lg animate-pulse"></div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Footer */}
          <div className="p-4 bg-gradient-to-r from-primary/5 to-primary/10 border-t border-gray-100">
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                Optimisé pour votre workflow
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Décembre 2024
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isExpanded && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => setIsExpanded(false)}
        ></div>
      )}
    </div>
  );
};

export default ModuleSwitch;